import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTennesseeMemphisDemoEnabled } from 'utils/featureFlags';
import {
  TNNG_MEMPHIS_EVENT_REGION,
  TNNG_MEMPHIS_EVENT_SUMMARY,
  TNNG_MEMPHIS_FACILITIES,
  TNNG_MEMPHIS_LINKED_EVENT_NAME,
  TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY,
  TNNG_MEMPHIS_THRESHOLD_DESCRIPTION,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/tennesseeMemphisThreatNotification';
import { orgEventKeys, type OrgEvent } from 'hooks/orgEvents/orgEventHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

function tnngMemphisCentroid(): { lon: number; lat: number } {
  const facilities = TNNG_MEMPHIS_FACILITIES;
  if (facilities.length === 0) {
    return { lon: -90.05, lat: 35.15 };
  }
  const lon =
    facilities.reduce((sum, facility) => sum + facility.lon, 0) /
    facilities.length;
  const lat =
    facilities.reduce((sum, facility) => sum + facility.lat, 0) /
    facilities.length;
  return { lon, lat };
}

function tnngResourcesCommittedSummary(): string {
  const preview = TNNG_MEMPHIS_FACILITIES.slice(0, 5)
    .map((facility) => `${facility.name} (${facility.city}, ${facility.state})`)
    .join('; ');
  const more =
    TNNG_MEMPHIS_FACILITIES.length > 5
      ? ` (+${TNNG_MEMPHIS_FACILITIES.length - 5} more)`
      : '';
  return `${TNNG_MEMPHIS_FACILITIES.length} Tennessee National Guard and supporting facilities in Memphis metro: ${preview}${more}`;
}

async function patchTnngMemphisOrgEventIfStale(
  existing: OrgEvent,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<OrgEvent> {
  const resourcesCommitted = tnngResourcesCommittedSummary();
  const needsPatch =
    existing.name !== TNNG_MEMPHIS_LINKED_EVENT_NAME ||
    existing.summary !== TNNG_MEMPHIS_EVENT_SUMMARY ||
    existing.region !== TNNG_MEMPHIS_EVENT_REGION ||
    existing.resources_committed !== resourcesCommitted;
  if (!needsPatch) {
    return existing;
  }

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: TNNG_MEMPHIS_LINKED_EVENT_NAME,
      summary: TNNG_MEMPHIS_EVENT_SUMMARY,
      region: TNNG_MEMPHIS_EVENT_REGION,
      resources_committed: resourcesCommitted,
    }
  );
  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(existing.group_guid),
  });
  return patched.data;
}

async function ensureTennesseeMemphisOrgEventForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<OrgEvent> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existing = (resp.data ?? []).find(
    (event) => event.demo_key === TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY
  );
  if (existing) {
    return patchTnngMemphisOrgEventIfStale(existing, queryClient);
  }

  const { lon, lat } = tnngMemphisCentroid();
  const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
    group_guid: groupGuid,
    name: TNNG_MEMPHIS_LINKED_EVENT_NAME,
    type: 'Flood',
    status: 'Active',
    severity: 'High',
    region: TNNG_MEMPHIS_EVENT_REGION,
    business_unit: 'Tennessee National Guard',
    summary: TNNG_MEMPHIS_EVENT_SUMMARY,
    resources_committed: tnngResourcesCommittedSummary(),
    lon,
    lat,
    creation_kind: 'threshold',
    created_by_user_guid: userGuid,
    threshold_description: TNNG_MEMPHIS_THRESHOLD_DESCRIPTION,
    demo_key: TNNG_MEMPHIS_ORG_EVENT_DEMO_KEY,
  });

  await queryClient.invalidateQueries({
    queryKey: orgEventKeys.list(groupGuid),
  });

  return created.data;
}

/** When TNG-only, ensure the Memphis flood/outage org event exists. */
export function useEnsureTennesseeMemphisOrgEvent() {
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tennesseeMemphisDemo || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `tnng-org-event:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureTennesseeMemphisOrgEventForGroup({
        groupGuid,
        userGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error(
          '[TNNG Memphis] Failed to ensure flood/outage org event',
          error
        );
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [tennesseeMemphisDemo, groupGuid, userGuid, queryClient]);
}
